'use client'

import { useEffect, useRef, useState } from 'react'
import { X, ListMusic, Trash2, GripVertical } from 'lucide-react'
import Artwork from './Artwork'
import { CoverImg } from './ui/kit'
import Marquee from './Marquee'
import { usePlayer } from '@/lib/player-context'
import { useTrackMenu } from './TrackMenu'

/** Tiroir « File d'attente » : ce qui joue + ce qui suit. Lecture au clic,
 *  retrait d'un morceau, menu contextuel (clic droit / appui long), et
 *  RÉORGANISATION par glisser-déposer (poignée à gauche) — souris ET tactile
 *  (pointer events), pour pouvoir remettre n'importe quel morceau en 1er, 2e… */
export default function QueueDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { queue, current, playing, play, removeFromQueue, reorderQueue } = usePlayer()
  const trackMenu = useTrackMenu()

  // Refs des lignes (pour calculer la cible du drop d'après la position Y).
  const rowsRef = useRef<Array<HTMLDivElement | null>>([])
  // dragIndex : ligne saisie. overIndex : curseur d'insertion (0..length).
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [overIndex, setOverIndex] = useState<number | null>(null)

  useEffect(() => {
    if (typeof document === 'undefined') return
    if (open) {
      document.body.style.overflow = 'hidden'
      const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
      document.addEventListener('keydown', onKey)
      return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', onKey) }
    }
  }, [open, onClose])

  if (!open) return null
  const curIdx = current ? queue.findIndex(t => t.videoId === current.videoId) : -1

  // Curseur d'insertion (0..length) selon la position verticale du pointeur :
  // on insère AVANT la 1re ligne dont le pointeur n'a pas dépassé le milieu.
  const computeOver = (clientY: number): number => {
    const rows = rowsRef.current
    for (let i = 0; i < queue.length; i++) {
      const el = rows[i]
      if (!el) continue
      const r = el.getBoundingClientRect()
      if (clientY < r.top + r.height / 2) return i
    }
    return queue.length
  }

  const onGripDown = (i: number) => (e: React.PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch {}
    setDragIndex(i)
    setOverIndex(i)
  }
  const onGripMove = (e: React.PointerEvent) => {
    if (dragIndex === null) return
    setOverIndex(computeOver(e.clientY))
  }
  const onGripUp = (e: React.PointerEvent) => {
    if (dragIndex !== null && overIndex !== null) reorderQueue(dragIndex, overIndex)
    setDragIndex(null)
    setOverIndex(null)
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId) } catch {}
  }

  const dragging = dragIndex !== null

  return (
    <div className="fixed inset-0 z-[1300] flex items-end sm:items-center sm:justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md border-t sm:border border-white/[0.08] rounded-t-[28px] sm:rounded-3xl flex flex-col max-h-[82vh] sm:max-h-[80vh] shadow-[0_24px_60px_rgba(0,0,0,.6),0_0_40px_rgb(var(--a2)/.15)]"
        style={{ animation: 'blcSheetUp 240ms cubic-bezier(0.22,0.8,0.24,1)', background: 'linear-gradient(180deg, #14163A 0%, rgb(var(--sbar)) 100%)' }}>
        <style>{`@keyframes blcSheetUp{from{transform:translateY(24px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>

        {/* poignée mobile */}
        <div className="sm:hidden flex justify-center pt-2.5"><span className="w-10 h-1 rounded-full bg-white/20" /></div>

        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.07]">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl grid place-items-center bg-gradient-to-br from-a2 to-a1 shadow-[0_6px_18px_rgb(var(--a2)/.45)]">
              <ListMusic size={19} className="text-white" />
            </span>
            <div>
              <div className="text-[18px] font-extrabold tracking-tight leading-tight">File d'attente</div>
              <div className="text-[11.5px] text-white/50">
                {queue.length} morceau{queue.length > 1 ? 'x' : ''}
                {queue.length > 1 && <span className="text-white/30"> · glisse ⠿ pour réorganiser</span>}
              </div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Fermer" className="w-9 h-9 rounded-full bg-white/[0.07] border border-white/10 hover:bg-white/[0.12] flex items-center justify-center text-white/75 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto px-2 py-2 flex-1" style={{ touchAction: dragging ? 'none' : undefined }}>
          {queue.length === 0 ? (
            <div className="text-center text-white/50 text-sm py-12">
              <ListMusic size={28} className="mx-auto text-white/25 mb-2" />
              File vide. Lance un morceau ou utilise « Lire ensuite ».
            </div>
          ) : (
            queue.map((t, i) => {
              const isCurrent = i === curIdx
              const upcoming = curIdx >= 0 && i > curIdx
              const isDragged = dragIndex === i
              const showLineBefore = dragging && overIndex === i && overIndex !== dragIndex && overIndex !== dragIndex + 1
              return (
                <div key={t.videoId + i} ref={el => { rowsRef.current[i] = el }}>
                  {/* Indicateur d'insertion (barre) AVANT cette ligne */}
                  {showLineBefore && <div className="mx-2 my-0.5 h-0.5 rounded-full bg-gradient-to-r from-a1 to-a3 shadow-[0_0_8px_rgb(var(--a3)/.8)]" />}
                  <div
                    className={`group flex items-center gap-2 px-2 py-2 rounded-xl transition ${isCurrent ? 'bg-spanel2 border border-a2/50' : 'hover:bg-white/[0.05] border border-transparent'} ${isDragged ? 'opacity-40' : ''}`}>
                    {/* Poignée de déplacement (souris + tactile) */}
                    <button
                      onPointerDown={onGripDown(i)}
                      onPointerMove={onGripMove}
                      onPointerUp={onGripUp}
                      onPointerCancel={onGripUp}
                      title="Glisser pour déplacer"
                      className="shrink-0 w-7 h-9 -ml-1 flex items-center justify-center text-white/30 hover:text-white/70 cursor-grab active:cursor-grabbing touch-none"
                      style={{ touchAction: 'none' }}>
                      <GripVertical size={16} />
                    </button>

                    <button onClick={() => play(t, queue)} {...trackMenu(t)} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                      <div className="relative shrink-0">
                        {t.isVideo
                          ? <span className="relative block w-12 h-12 rounded-xl overflow-hidden bg-spanel3"><CoverImg src={t.thumbnail} videoId={t.videoId} /></span>
                          : <Artwork track={t} size={48} strict rounded="rounded-xl" className="shrink-0" />}
                        {isCurrent && playing && (
                          <span className="absolute inset-0 flex items-center justify-center bg-adeep/60 rounded-xl">
                            <span className="flex items-end gap-0.5 h-3.5">
                              {[0, 0.15, 0.3].map((d, k) => <span key={k} className="w-[3px] rounded-sm bg-white animate-equalizer" style={{ height: '100%', animationDelay: `${d}s` }} />)}
                            </span>
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <Marquee className={`text-sm font-semibold ${isCurrent ? 'text-a5' : ''}`}>{t.title}</Marquee>
                        <Marquee className="text-xs text-white/50">{t.artist}</Marquee>
                      </div>
                      {isCurrent
                        ? <span className="text-[9.5px] tracking-wider text-white font-bold shrink-0 px-2 py-0.5 rounded-full bg-gradient-to-r from-a1 to-a2">EN LECTURE</span>
                        : upcoming ? <span className="text-[9px] tracking-widest text-white/35 shrink-0 pr-1">À SUIVRE</span> : null}
                    </button>
                    {!isCurrent && (
                      <button onClick={() => removeFromQueue(t.videoId)} title="Retirer de la file"
                        className="shrink-0 w-8 h-8 rounded-lg text-white/40 hover:text-a5 hover:bg-a2/15 flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  {/* Indicateur d'insertion EN FIN de liste */}
                  {dragging && i === queue.length - 1 && overIndex === queue.length && dragIndex !== queue.length - 1 && (
                    <div className="mx-2 my-0.5 h-0.5 rounded-full bg-gradient-to-r from-a1 to-a3 shadow-[0_0_8px_rgb(var(--a3)/.8)]" />
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
